import React, { useEffect, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { Modal } from './ui/Modal';
import OsScroll from './ui/OsScroll';
import { listEvents } from '../extensions/bundled/calendar/calendarStore';
import {
  createInvitations,
  buildSnapshotFromEvent,
  surfaceInviteError,
  invitesBlockedFor,
} from '../services/calendarInvitesService';
import { buildAppointmentShareMetadata } from '../utils/appointmentChatShare';
import { formatEventTime, formatDayLong, eventRange, eventsInRange, addDays, startOfDay } from '../utils/calendarDates';
import { useAuthStore } from '../store/authStore';
import { usePreferencesStore } from '../store/preferencesStore';
import { useNotificationStore } from '../store/notificationStore';
import chatService from '../services/chatService';

const LOOKAHEAD_DAYS = 60;

function shareUid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `appt-share-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Composer picker for sharing an appointment into the current conversation
 * (PLAN §3.4 send flow, in ITS OWN file — ChatMessageView is a megafile;
 * add beside, not into). Lists the viewer's upcoming events; picking one
 * runs the two-step order the share modals use: create the invitations
 * FIRST (`channel_id` expansion server-side), and only on 201 send the chat
 * card via the REST message path (chatService.js sendMessage). A 402 (free
 * organizer) therefore never produces a card for an invitation that was
 * never created.
 */
export default function AppointmentSharePicker({ channelId, onClose, onSent }) {
  const [upcoming, setUpcoming] = useState(null); // null = loading
  const [busyUid, setBusyUid] = useState(null);
  const timeFormat = usePreferencesStore((s) => s.timeFormat);
  const currentUserId = useAuthStore((s) => s.user?.id);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const events = await listEvents();
        const from = startOfDay(new Date());
        const to = addDays(from, LOOKAHEAD_DAYS);
        const rows = eventsInRange(events, from, to)
          // Only my OWN PERSONAL events: team events carry no invitations in
          // v1 (visibility is membership), materialized copies aren't mine.
          .filter(
            (e) =>
              e.status !== 'cancelled' &&
              !e.origin &&
              (e.calendar || 'personal') === 'personal' &&
              // Phase 4 (P4-N1): recurring masters AND their overrides are
              // uninvitable in v1 — one shared predicate at every gate.
              !invitesBlockedFor(e)
          )
          .slice(0, 30);
        if (!cancelled) setUpcoming(rows);
      } catch {
        if (!cancelled) setUpcoming([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const share = async (event) => {
    if (busyUid) return;
    setBusyUid(event.uid);
    const { success } = useNotificationStore.getState();
    try {
      // 1) invitations for the channel's members (server expands + notifies);
      await createInvitations({
        appointmentUid: event.uid,
        snapshot: buildSnapshotFromEvent(event),
        channelId,
      });
      // 2) only then the card — REST path, share-modal order.
      await chatService.sendMessage(channelId, {
        uid: shareUid(),
        body: '',
        kind: 'share',
        metadata: buildAppointmentShareMetadata({ event, organizerId: currentUserId }),
      });
      success('Appointment shared');
      onSent?.();
      onClose?.();
    } catch (err) {
      // 402 → PlanUpgradeModal, everything else → the server's named string.
      surfaceInviteError(err, 'Failed to share appointment');
    } finally {
      setBusyUid(null);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Share an appointment" size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
        {upcoming == null && <span style={{ color: '#9ca3af' }}>Loading events…</span>}
        {upcoming != null && upcoming.length === 0 && (
          <span style={{ color: '#9ca3af' }}>
            No upcoming events in the next {LOOKAHEAD_DAYS} days. Create one in the Calendar
            first.
          </span>
        )}
        {upcoming != null && upcoming.length > 0 && (
          <OsScroll style={{ maxHeight: 320 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {upcoming.map((event) => (
                <button
                  key={event.uid}
                  type="button"
                  className="cal-btn"
                  style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  disabled={busyUid != null}
                  onClick={() => share(event)}
                >
                  <CalendarDays size={13} style={{ flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {event.title || '(no title)'}
                  </span>
                  <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 11, flexShrink: 0 }}>
                    {formatDayLong(eventRange(event).start)} · {formatEventTime(event, timeFormat)}
                  </span>
                </button>
              ))}
            </div>
          </OsScroll>
        )}
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          Everyone in this conversation gets an invitation they can accept or decline.
        </span>
      </div>
    </Modal>
  );
}
